import * as styles from "./AllFilters.module.scss";

import React, { useEffect } from "react";
import { Button, Form } from "../../../components/common";

import FilterOption from "../../../components/FilterOption/FilterOption";
import { Info } from "../../../components/common/Icons";
import LoadingIndicator from "../../../components/common/LoadingIndicator/LoadingIndicator";
import { useDogSearchApi } from "../../../context";
import { DISTANCE_OPTIONS } from "../../../context/DogSearchApi/DogSearchApi.fixtures";
import { isSpecificSite } from "../../../utils/isSpecificSite";
import LocationInput from "../LocationInput/LocationInput";

export const AllFilters: React.FC = () => {
	isSpecificSite("uk", "AllFilters");

	const {
		isListingPage,

		selectedBreeds,
		selectedAges,
		selectedSizes,
		selectedGenders,
		selectedLivesWith,
		selectedCentres,
		selectedDayRange,
		resultsCount,
		currentDistance,
		searchListingsUrl,
		dogApiLoading,

		userLocation,
		isUnderdog,
		noReserved,

		onUpdateTravelDistance,
		onUpdateLocation,

		useDogSearchQuery,
		onChangeFilterView,
		getLivesWithDisabled,
		onUpdateAge,
		onUpdateSizes,
		onUpdateGenders,
		onUpdateLivesWith,
		onUpdateDayRange,
		isFilterDisabled,
		onShowDogs,
		onToggleIsUnderdog,
		onToggleNoReserved,
	} = useDogSearchApi();

	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want this to run once
	useEffect(() => {
		useDogSearchQuery();
	}, []);

	const trackFilters = (data: Google.DataLayerObject) => {
		// biome-ignore lint/suspicious/noExplicitAny: Don't have the type
		(window as any).dataLayer = (window as any).dataLayer || [];
		// biome-ignore lint/suspicious/noExplicitAny: Don't have the type
		(window as any).dataLayer.push({ ...data });
	};
	const getShowDogsProps = () => {
		return isListingPage
			? {
					onClick: () => {
						onShowDogs();
						trackFilters({
							event: "rehoming_search",
							rehoming_location: selectedCentres?.toString(), // location selected
							rehoming_breed: selectedBreeds?.toString(), // dog breed selected, comma separated list for multiple selections
							rehoming_age: selectedAges?.toString(), // dog age selected, comma separated list for multiple selections
							rehoming_size: selectedSizes?.toString(), // dog size selected, comma separated list for multiple selections
							rehoming_gender: selectedGenders?.toString(), // gender selected
							rehoming_live_with: selectedLivesWith?.toString(), // may live with preference selected, comma separated listfor multiple selections
							rehoming_days_added: selectedDayRange?.toString(), // days since added selection
							rehoming_distance: currentDistance?.toString(), // The distance the user is willing to travel
						});
					},
				}
			: { url: searchListingsUrl };
	};

	return (
		<>
			<div className={styles.filteroptionswrapper}>
				<Form onSubmitFn={() => {}} validationSchema={{}}>
					<div
						className={styles.loadingoverlay}
						style={{ display: dogApiLoading ? "flex" : "none" }}
					>
						<div className={styles.loadingbackground} />
						<div className={styles.loadingicon}>
							<LoadingIndicator loading={dogApiLoading} scale={1.5} />
						</div>
					</div>
					<div className={styles.filteroptions}>
						<div className={styles.location}>
							<label htmlFor="location" className={styles.sectiontitle}>
								Postcode
							</label>
							<LocationInput
								name={"location"}
								userLocation={userLocation}
								className={styles.searchinput}
								onClearUserLocation={() => onUpdateLocation(null, 1000)}
								onConfirmUserLocation={(location) =>
									onUpdateLocation(
										location,
										currentDistance > 150 ? 150 : currentDistance,
									)
								}
								placeholder={"Enter a postcode"}
							/>
						</div>
						<div className={styles.location}>
							<label htmlFor="distance-input" className={styles.sectiontitle}>
								Distance
							</label>
							<select
								id="distance-input"
								name="distance"
								className={styles.distanceselect}
								value={currentDistance}
								onChange={(e) =>
									onUpdateTravelDistance(Number.parseInt(e.currentTarget.value))
								}
								disabled={!userLocation}
								style={{}}
							>
								{!userLocation ? (
									<option>Any</option>
								) : (
									<>
										{DISTANCE_OPTIONS.map((option) => (
											<option key={option} value={option}>
												{option} miles
											</option>
										))}
									</>
								)}
							</select>
						</div>
						<fieldset className={styles.breed}>
							<legend className={styles.sectiontitle}>Breed</legend>
							<button
								type="button"
								className={styles.breedorlocationselector}
								onClick={() => onChangeFilterView("Breeds")}
								onKeyDown={(e) =>
									e.key === "Enter" && onChangeFilterView("Breeds")
								}
							>
								<div className={styles.breedtext}>
									<div>Breeds selected</div>
									<div className={styles.breedselectedcount}>
										{selectedBreeds.length}
									</div>
								</div>
								<div className={styles.addbreedsicon}>+</div>
							</button>
						</fieldset>
						<fieldset className={styles.age}>
							<legend className={styles.sectiontitle}>Age</legend>
							<div className={styles.ageoptions}>
								{[
									"Under 6 months",
									"6 - 12 months",
									"1 - 2 years",
									"2 - 5 years",
									"5 - 7 years",
									"8 + years",
								].map((age) => (
									<FilterOption
										key={age}
										option={age}
										className={styles.ageoption}
										ariaPressed={selectedAges.includes(age)}
										variant={
											selectedAges.includes(age) ? "filterselected" : "filter"
										}
										onClick={() => onUpdateAge(age)}
										disabled={
											isFilterDisabled("AGE", age) &&
											!selectedAges.includes(age)
										}
									/>
								))}
							</div>
						</fieldset>
						<fieldset className={styles.size}>
							<legend className={styles.sectiontitle}>Size</legend>
							<div className={styles.sizeheader}>
								<button
									type="button"
									className={styles.sizeguide}
									onClick={() => onChangeFilterView("SizeGuide")}
									tabIndex={0}
									onKeyDown={(e) =>
										e.key === "Enter" && onChangeFilterView("SizeGuide")
									}
								>
									<div className={styles.sizeguidecontent}>
										<div className={styles.sizeguide}>Size Guide</div>
										<Info height={"16px"} width={"16px"} />
									</div>
								</button>
							</div>
							<div className={styles.sizeoptions}>
								{["Small", "Medium", "Large", "Giant"].map((size) => (
									<FilterOption
										key={size}
										option={size}
										className={styles.sizeoption}
										ariaPressed={selectedSizes.includes(size)}
										variant={
											selectedSizes.includes(size) ? "filterselected" : "filter"
										}
										onClick={() => onUpdateSizes(size)}
										disabled={
											isFilterDisabled("SIZE", size) &&
											!selectedSizes.includes(size)
										}
									/>
								))}
							</div>
						</fieldset>
						<fieldset className={styles.gender}>
							<legend className={styles.sectiontitle}>Gender</legend>
							<div className={styles.genderoptions}>
								{["Male", "Female"].map((gender) => (
									<FilterOption
										key={gender}
										option={gender}
										className={styles.genderoption}
										ariaPressed={selectedGenders.includes(gender)}
										variant={
											selectedGenders.includes(gender)
												? "filterselected"
												: "filter"
										}
										onClick={() => onUpdateGenders(gender)}
										disabled={
											isFilterDisabled("GENDER", gender) &&
											!selectedGenders.includes(gender)
										}
									/>
								))}
							</div>
						</fieldset>
						<fieldset className={styles.maylivewith}>
							<legend className={styles.sectiontitle}>May live with</legend>
							<div className={styles.maylivewithoptions}>
								{["Dogs", "Cats"].map((liveWith) => (
									<FilterOption
										key={liveWith}
										option={liveWith}
										className={styles.maylivewithoptiontwocol}
										ariaPressed={selectedLivesWith.includes(liveWith)}
										variant={
											selectedLivesWith.includes(liveWith)
												? "filterselected"
												: "filter"
										}
										onClick={() => onUpdateLivesWith(liveWith)}
										disabled={
											getLivesWithDisabled(liveWith) &&
											!selectedLivesWith.includes(liveWith)
										}
									/>
								))}
								{[
									"Pre-school children",
									"Primary school children",
									"Secondary school children",
								].map((liveWith) => (
									<FilterOption
										key={liveWith}
										option={liveWith}
										className={styles.maylivewithoptiononecol}
										ariaPressed={selectedLivesWith.includes(liveWith)}
										variant={
											selectedLivesWith.includes(liveWith)
												? "filterselected"
												: "filter"
										}
										onClick={() => onUpdateLivesWith(liveWith)}
										disabled={
											getLivesWithDisabled(liveWith) &&
											!selectedLivesWith.includes(liveWith)
										}
									/>
								))}
							</div>
						</fieldset>
						<fieldset className={styles.dayssinceadded}>
							<legend className={styles.sectiontitle}>Days since added</legend>
							<div className={styles.dayssinceaddedoptions}>
								{["Any", "1", "3", "7", "14", "30"].map((dayOption) => (
									<div key={dayOption} className={styles.dayssinceaddedoption}>
										<input
											id={dayOption}
											type="radio"
											name={"days"}
											value={dayOption}
											defaultChecked={selectedDayRange === dayOption}
											onChange={() => onUpdateDayRange(dayOption)}
										/>
										<label htmlFor={dayOption}>{dayOption}</label>
									</div>
								))}
							</div>
						</fieldset>
						<fieldset className={styles.advancedfilters}>
							<legend className={styles.sectiontitle}>
								Additional filters
							</legend>
							<div>
								<div className={styles.checkbox}>
									<input
										id="hide-reserved"
										name="hide-reserved"
										type="checkbox"
										value="hide-reserved"
										onChange={() => onToggleNoReserved(!noReserved)}
										checked={noReserved}
										disabled={isFilterDisabled("IS_NO_RESERVED")}
									/>
									<label
										htmlFor="hide-reserved"
										className={
											isFilterDisabled("IS_NO_RESERVED")
												? styles.disabledcheckboxlabel
												: styles.checkboxlabel
										}
									>
										{"Hide reserved dogs"}
									</label>
								</div>
								<div className={styles.checkbox}>
									<input
										id="only-underdogs"
										name="only-underdogs"
										type="checkbox"
										value="only-underdogs"
										onChange={() => onToggleIsUnderdog(!isUnderdog)}
										checked={isUnderdog}
										disabled={isFilterDisabled("IS_UNDERDOGS")}
									/>
									<label
										htmlFor="only-underdogs"
										className={
											isFilterDisabled("IS_UNDERDOGS")
												? styles.disabledcheckboxlabel
												: styles.checkboxlabel
										}
									>
										{"Show only underdogs"}
									</label>
								</div>
							</div>
						</fieldset>
					</div>
				</Form>
			</div>
			<div className={styles.showresults}>
				<div className={styles.buttonwrapper}>
					<Button
						fullWidth={true}
						{...getShowDogsProps()}
						isLoading={dogApiLoading}
					>{`Show (${resultsCount}) matching dogs`}</Button>
					<span className={styles.accessibilityalert} role="alert">
						{dogApiLoading
							? "Updating Dog data"
							: `${resultsCount} dogs match your filters`}
					</span>
				</div>
			</div>
		</>
	);
};

export default AllFilters;
